/**
 * Which read owns the state it is about to write.
 *
 * A store field filled by an async read has no way, on its own, to tell the answer it is waiting
 * for from an answer it stopped waiting for. Every read in this directory wrote whatever came back,
 * so a list that was asked twice on one arrival — and several are, by a mount and a paginator event
 * and a sort event that all land together — showed whichever request the network happened to
 * finish last. The same gap is what lets a slow page 1 land after a fast page 2 and leave the
 * reader on page 2 looking at page 1's rows.
 *
 * A read claims a token before it starts and checks it still holds it before it writes; a read that
 * does not is dropped, including its error and its `stopLoadingAndFinish`, so a superseded failure
 * cannot fail the list the current read is about to fill.
 *
 * Lifted from `caseNote.ts`, which had grown the only correct version of this in the directory. One
 * counter per group of fields that answer the same question, not one per function: two reads that
 * fill the same list and drive the same loader are one conversation, and the newest ask wins it.
 */
export interface RequestToken {
  /** Start a read. The returned token is the only one `holds` will accept until the next claim. */
  claim(): number;
  /** Whether this read is still the one whose answer the store wants. */
  holds(token: number): boolean;
  /**
   * Give up on whatever is in flight without starting anything in its place.
   *
   * The same move as `claim` with the token thrown away, and a separate name because the call site
   * means something different by it: not "I am reading now" but "the answer I was waiting for is one
   * I no longer want". `caseNote.ts` and `perfusionScan.ts` clearing their panel are the callers.
   */
  supersede(): void;
}

export const createRequestToken = (): RequestToken => {
  let latest = 0;

  return {
    claim: () => ++latest,
    holds: (token: number) => token === latest,
    supersede: () => {
      latest++;
    }
  };
};
